import React, { forwardRef, useState } from "react";
import MaskedInput from 'react-text-mask';

const EyeIcon = ({ open }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    )}
  </svg>
);

const FormInput = forwardRef(({
  label="Label", type="text", name="", placeholder="", help="",
  float=false, value="", onChange, onBlur, readOnly=false, mask,
  dataIugu="", required=false,
  // NOVOS:
  cardDigits = "",
  cardType = "",
  onCardTypeChange, // <- callback extra (não substitui onChange)
  onlyNumbers = false,
  ...rest
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword ? (showPassword ? "text" : "password") : type;

  const floatClass = float ? "form-floating" : "form-group";

  const cardGroupsToMask = (groups) => {
    const out = [];
    groups.forEach((g, gi) => {
      for (let i = 0; i < g; i++) out.push(/\d/);
      if (gi < groups.length - 1) out.push(' ');
    });
    return out;
  };

  const detectCardByDigits = (digits) => {
    if (/^3[47]\d{0,13}$/.test(digits)) return 'amex';
    if (/^(34|37)/.test(digits)) return 'amex';
    if (/^4\d{0,15}$/.test(digits)) return 'visa';
    if (/^5[1-5]\d{0,14}$/.test(digits) || /^2(2[2-9]|[3-6]\d|7[01]|720)\d{0,14}$/.test(digits)) return 'mastercard';
    if (/^3(?:0[0-5]|[68])\d{0,11}$/.test(digits)) return 'diners';
    if (/^6(?:011|5|4[4-9])\d{0,12}$/.test(digits)) return 'discover';
    if (/^(?:2131|1800|35)\d{0,13}$/.test(digits)) return 'jcb';
    return 'unknown';
  };

  const createMask = () => {
    switch (mask) {
      case '99/99/9999': return [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /[1-2]/, /\d/, /\d/, /\d/];
      case '9.99': return [/\d/, '.', /\d/, /\d/];
      case '999': return [/\d/, /\d/, /\d/];
      case 'credit_card_dynamic':
        return (rawValue) => {
          const digits = (rawValue || '').replace(/\D/g, '');
          const card = detectCardByDigits(digits);
          if (card === 'amex') return cardGroupsToMask([4,6,5]);
          if (card === 'diners') return cardGroupsToMask([4,6,4]);
          return cardGroupsToMask([4,4,4,4]);
        };
      case 'cvv_dynamic': {
        const digits = (cardDigits || '').replace(/\D/g, '');
        const type = cardType || detectCardByDigits(digits);
        return type === 'amex' ? [/\d/, /\d/, /\d/, /\d/] : [/\d/, /\d/, /\d/];
      }
      case 'credit_card':
        return [/\d/,/\d/,/\d/,/\d/,' ',/\d/,/\d/,/\d/,/\d/,' ',/\d/,/\d/,/\d/,/\d/,' ',/\d/,/\d/,/\d/,/\d/];
      case 'cvv': return [/\d/, /\d/, /\d/];
      case 'expiration': return [/[0-1]/, /[0-9]/, '/', /[2-3]/, /[0-9]/];
      case 'cep': return [/\d/,/\d/,/\d/,/\d/,/\d/,'-',/\d/,/\d/,/\d/];
      case 'cpf': return [/\d/,/\d/,/\d/,'.',/\d/,/\d/,/\d/,'.',/\d/,/\d/,/\d/,'-',/\d/,/\d/];
      default: return false;
    }
  };

  const maskPattern = createMask();

  const handleChange = (e) => {
    if (onlyNumbers && !mask) {
      const digits = (e.target.value || '').replace(/\D/g, '');
      if (digits !== e.target.value) {
        e.target.value = digits;
      }
    }

    onChange && onChange(e);
    if (mask === 'credit_card_dynamic' && typeof onCardTypeChange === 'function') {
      const digits = (e.target.value || '').replace(/\D/g, '');
      onCardTypeChange({
        type: detectCardByDigits(digits),
        digits
      });
    }
  };

  const commonProps = {
    className:"form-control", id:name, name, placeholder, value,
    onChange: handleChange, onBlur, ref,
    ...(dataIugu ? { 'data-iugu': dataIugu } : {}),
    readOnly, required,
    ...rest
  };

  return (
    <div className={floatClass} style={isPassword ? { position: "relative" } : undefined}>
      {maskPattern ? (
        <MaskedInput mask={maskPattern} {...commonProps} />
      ) : (
        <input type={effectiveType} {...commonProps} />
      )}
      {float && <label htmlFor={name}>{label}</label>}
      {!float && help && <small id={`${name}Help`} className="form-text text-muted">{help}</small>}
      {isPassword && (
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          style={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            color: "#888",
            display: "flex",
            alignItems: "center",
          }}
          tabIndex={-1}
          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
        >
          <EyeIcon open={showPassword} />
        </button>
      )}
    </div>
  );
});

export default FormInput;
